import m from '../m.js'
import { auth, toasts } from '../api.js'
import { board, repos } from '../store.js'
import { icon } from '../icons.js'
import { settingsDialogView } from './settings.js'
import { avatarView, pickerView } from './widgets.js'

function loginView() {
  return m('.login', [
    m('h1', 'Kanban'),
    m('p.muted', m.t('app.tagline')),
    m(
      'a.button.primary',
      {
        href: `/auth/login?return_to=${encodeURIComponent(location.pathname + location.search)}`,
      },
      [icon('mark-github'), m.t('app.signIn')]
    ),
  ])
}

function toastsView() {
  return m(
    '.toasts',
    toasts.list.map(toast => m('.toast', { class: toast.kind }, toast.text))
  )
}

let settingsOpen = false

// paths the server answers itself, not routes of the app
const SERVER_PATHS = /^\/(api|auth|setup|u|webhooks)\//

/**
 * In-app links navigate without a page load: every <a href="/…"> of this origin, from views
 * and from rendered markdown alike. New tabs, downloads, other sites and clicks a handler
 * already took care of (preventDefault) keep the browser's behavior.
 */
function followLink(evt) {
  evt.redraw = false
  if (
    evt.defaultPrevented ||
    evt.button !== 0 ||
    evt.metaKey ||
    evt.ctrlKey ||
    evt.shiftKey ||
    evt.altKey
  )
    return
  const a = evt.target.closest('a[href]')
  if (
    !a ||
    a.target ||
    a.hasAttribute('download') ||
    a.origin !== location.origin ||
    SERVER_PATHS.test(a.pathname)
  )
    return
  evt.preventDefault()
  m.route.set(a.pathname + a.search)
}

/** Page frame: top bar with repo picker and the page (`children`) below. */
export function layoutView(attrs, children) {
  if (auth.user === undefined) return null
  if (!auth.user) return [loginView(), toastsView()]
  repos.load()
  const current = attrs.owner ? `${attrs.owner}/${attrs.repo}` : ''
  const known = repos.list?.some(
    r => r.fullName.toLowerCase() === current.toLowerCase()
  )
  return m('.app', { onclick: followLink }, [
    m('header.topbar', [
      m(
        'a.brand',
        { href: '/', title: m.t('app.home') },
        m('img', {
          src: '/favicon.svg',
          width: 32,
          height: 32,
          alt: m.t('app.home'),
        })
      ),
      pickerView({
        className: 'repo-select',
        align: 'left',
        multi: false,
        heading: m.t('repo.switch'),
        selected: known
          ? [
              repos.list.find(
                r => r.fullName.toLowerCase() === current.toLowerCase()
              ).fullName,
            ]
          : [],
        options: (repos.list || []).map(r => ({
          value: r.fullName,
          label: r.fullName,
          view: () => [icon(r.private ? 'lock' : 'repo'), r.fullName],
        })),
        onchange: ([repo]) => repo && m.route.set(`/${repo}`),
        toggle: [
          icon('repo'),
          current || (repos.list ? m.t('repo.select') : m.t('detail.loading')),
          icon('triangle-down'),
        ],
      }),
      current &&
        board.data &&
        m(
          'a.button.icon-button',
          {
            href: board.data.repo.url,
            target: '_blank',
            title: m.t('repo.openOnGithub', { repo: current }),
            'aria-label': m.t('repo.openOnGithub', { repo: current }),
          },
          icon('mark-github')
        ),
      m('.spacer'),
      avatarView({ user: auth.user, size: 24 }),
      m('span.login-name', auth.user.login),
      m(
        'button.icon-button',
        {
          title: m.t('settings.open'),
          'aria-label': m.t('settings.open'),
          onclick: () => (settingsOpen = true),
        },
        icon('gear')
      ),
      m(
        'button.icon-button',
        {
          title: m.t('app.signOut'),
          'aria-label': m.t('app.signOut'),
          onclick: () => auth.logout(),
        },
        icon('sign-out')
      ),
    ]),
    m('main', children),
    settingsOpen &&
      settingsDialogView({ onclose: () => (settingsOpen = false) }),
    toastsView(),
  ])
}

let redirected = false

export const homeView = m.withState(() => {
  // jump to the last board once per page load; the brand link still reaches this list
  let last = null
  try {
    last = localStorage.getItem('lastRepo')
  } catch {
    // storage unavailable (private mode, blocked): works without it
  }
  if (last && !redirected) m.route.set(`/${last}`, null, { replace: true })
  redirected = true

  return () => {
    if (!repos.list) return m('.center.muted', m.t('repo.loading'))
    if (!repos.list.length) {
      return m(
        '.center',
        m('.empty', [
          m('h2', m.t('repo.none')),
          m('p.muted', m.t('repo.noneHint')),
        ])
      )
    }
    return m('.repo-list', [
      m('h2', m.t('repo.list')),
      repos.list.map(r =>
        m('a.repo-link', { href: `/${r.fullName}` }, [
          r.fullName,
          r.private && m('span.muted', ` · ${m.t('repo.private')}`),
        ])
      ),
    ])
  }
})
